/**
 * Dropdown selector component.
 */
import React, { useCallback, useEffect, useState } from 'react';

import { FlatList, ListRenderItemInfo, Platform, ViewStyle } from 'react-native';

import { Colors } from 'theme/colors';
import { FontSize } from 'theme/fonts';
import { Container, Fill } from 'theme/layout';
import { Bold } from 'theme/text';
import { ACTIVE_OPACITY } from 'utils/constants';
import { Option } from '../models';
import { ValuePickerProps } from '../models/picker';
import { DropdownItem } from './dropdownItem';
import {
  ButtonContainer,
  ButtonIconCircle,
  DropdownContainer,
  DropdownContent,
  RotatingIcon,
  RoundedCardBase,
} from './styles';

const MAX_HEIGHT = 400;
const DEFAULT_OPTIONS = [
  {
    key: 'Not Available',
    value: undefined,
  },
];

// TODO: Fix the complexity of this component
/**
 * Dropdown component for option selection.
 *
 * **Note:** This was built as a multi-selector for performance,
 * but by-design is limited to only single selection.
 *
 * @param props - Dropdown picker props.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function DropdownPicker<T>(props: DropdownPickerProps<T>) {
  const { selectedValue, options, marginHorizontal, maximumAllowed } = props;
  let { disabled } = props;
  const _selectedItem = options.find(option => option.value === selectedValue);
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState(
    _selectedItem ? new Map<T, boolean>([[selectedValue, true]]) : new Map<T, boolean>()
  );
  const [selectedItem, setSelectedItem] = useState(_selectedItem);
  const cardStyle: ViewStyle = {
    maxHeight: expanded ? MAX_HEIGHT : 0,
    ...Platform.select({
      web: { overflowX: 'hidden', overflowY: 'auto' },
    }),
    borderRadius: 15,
    marginTop: 5,
  };

  if (!selectedItem) {
    disabled = true;
  }

  // Determine if button should be interactive
  const isSingleOption = options.length <= 1;
  const shouldDisableInteraction = disabled || isSingleOption;

  // Use normal color for single option (no grey out), grey only for truly disabled
  const excessColor = disabled && !isSingleOption ? Colors.gray : Colors.black;

  useEffect(() => {
    const item = options.find(option => option.value === selectedValue);

    setSelectedItem(item);
  }, [selectedValue, options]);

  const toggle = useCallback(() => {
    if (!isSingleOption) {
      setExpanded(!expanded);
    }
  }, [expanded, isSingleOption]);

  const handleOnPress = useCallback(
    (item: Option<T>) => {
      const { value } = item;
      const { onChange } = props;

      if (!value) {
        return;
      }

      // Copy existing map rather than modifying state
      const _selected = new Map(selected);

      if (selectedItem && selectedItem.value) {
        _selected.delete(selectedItem.value);
      }

      _selected.set(value, !selected.get(value)); // Toggle selection

      if (onChange) {
        onChange(item.value);
      }

      setSelected(_selected);
      setSelectedItem(item);
      setExpanded(false);
    },
    [props, selected, selectedItem]
  );

  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<Option<T>>) => {
      let _disabled = false;

      if (!item.value) {
        return null;
      }

      if (maximumAllowed) {
        _disabled = typeof item.value === 'number' && item.value > maximumAllowed;
      }

      return (
        <DropdownItem
          item={item}
          disabled={_disabled}
          onPress={handleOnPress}
          selected={Boolean(selected.get(item.value))}
        />
      );
    },
    [selected, handleOnPress, maximumAllowed]
  );

  return (
    <Container>
      <ButtonContainer
        disabled={shouldDisableInteraction}
        onPress={toggle}
        activeOpacity={isSingleOption ? 1.0 : ACTIVE_OPACITY}
        backgroundColor={Colors.gallery}
        marginHorizontal={marginHorizontal}
      >
        <Fill style={{ justifyContent: 'center' }}>
          <Bold color={excessColor} textAlign="center" fontSize={FontSize.H5}>
            {selectedItem ? selectedItem.key : DEFAULT_OPTIONS[0].key}
          </Bold>
        </Fill>
        {options.length > 1 && (
          <ButtonIconCircle backgroundColor={Colors.white}>
            <RotatingIcon expanded={expanded} color={excessColor} />
          </ButtonIconCircle>
        )}
      </ButtonContainer>
      {options.length > 1 && (
        <RoundedCardBase style={cardStyle}>
          <DropdownContainer>
            <DropdownContent>
              <FlatList
                data={options}
                keyExtractor={keyExtractor}
                extraData={selected}
                renderItem={renderItem}
              />
            </DropdownContent>
          </DropdownContainer>
        </RoundedCardBase>
      )}
    </Container>
  );
}
/**
 * Used to extract a unique key for a given item at the specified index.
 * Key is used for caching and as the react key to track item re-ordering.
 *
 * @param item - Current item to identify.
 * @param _index - Current index of the item.
 */
function keyExtractor<T>(item: Option<T>, _index: number) {
  return item.key;
}
export interface DropdownPickerProps<T> extends ValuePickerProps<T> {
  /** A maximum value used to disable options that are too high (number option picker only) */
  maximumAllowed?: number;
  /** Horizontal margin */
  marginHorizontal?: number;
}
